import { ICellRendererParams, RowNode } from 'ag-grid-community';
import { SelectListCellRenderer } from './renderer/select-list-cell.renderer';
import { TextBoxCellRenderer } from './renderer/text-box-cell.renderer';
import { EditableGridService } from './service/editable-grid.service';
import { GridBaseComponent } from './grid-base.component';

export abstract class EditableGridComponent extends GridBaseComponent {

	protected originalConfig: Array<any>;
	protected config: Array<any>;
	protected SELECTED_NODE_HEIGHT = 250;
	protected INCREMENT_GRID_HEIGHT_ON_EXPAND = 212;
	protected expandedRow: RowNode;
	protected willCollapseRow: RowNode;
	private service: EditableGridService;

	protected constructor(service: EditableGridService) {
		super();
		this.service = service;
		this.originalConfig = [];
		this.config = [];
		this.gridOptions.context = {parentComponent: this};
		this.gridOptions.frameworkComponents = {
			'selectListCellRenderer': SelectListCellRenderer,
			'textBoxCellRenderer': TextBoxCellRenderer,
		};
	}

	/**
	 * Expand Row node
	 * @param row
	 */
	expandRow(row: RowNode): void {
		if (this.willCollapseRow && this.willCollapseRow.rowIndex === row.rowIndex) {
			this.willCollapseRow = null;
		}
		if (!this.expandedRow || this.expandedRow.rowIndex !== row.rowIndex) {
			this.expandedRow = row;
			this.gridApi.resetRowHeights();
			this.gridHeight = this.gridHeight + this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
		}
	}

	/**
	 * Returns the current expanded row.
	 */
	getExpandedRow(): RowNode {
		return this.expandedRow;
	}

	/**
	 * Collapse the Row node
	 * @param row
	 */
	collapseRow(row: RowNode): void {
		if (this.expandedRow && row.rowIndex === this.expandedRow.rowIndex) {
			this.expandedRow = null;
			this.gridApi.resetRowHeights();
		}
	}

	/**
	 * This method was created to avoid the collision of the open/close events of a select list
	 * from a different column but the same row.
	 * Collapse the Row node
	 * @param row
	 */
	collapseRowWithDelay(row: RowNode): void {
		this.willCollapseRow = row;
		setTimeout(() => {
			if (this.willCollapseRow !== null) {
				this.collapseRow(this.willCollapseRow);
				this.willCollapseRow = null;
				this.gridHeight = this.gridHeight - this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
			}
		}, 200);
	}

	/**
	 * On Row Selected.
	 * @param $event
	 */
	onRowSelected($event: any): void {
		// Silence is golden.
	}

	/**
	 * Sets the row height.
	 * @param params
	 */
	getRowHeight = (params: any): number => {
		const rowNode: RowNode = params.node;
		if (this.expandedRow && rowNode.rowIndex === this.expandedRow.rowIndex) {
			return this.SELECTED_NODE_HEIGHT;
		} else {
			return this.DEFAULT_ROW_HEIGHT;
		}
	}

	/**
	 * Is configuration dirty?
	 */
	isDirty(): boolean {
		return JSON.stringify(this.originalConfig) !== JSON.stringify(this.config);
	}

	/**
	 * Emit is dirty through service.
	 */
	emitDirtyness(): void {
		this.service.setFormData({isDirty: this.isDirty()});
	}

	/**
	 * Implement logic when cell gets updated.
	 * @param param
	 * @param newData
	 */
	abstract onCellUpdated(param: ICellRendererParams, newData: any): void;
}
